/**
 * INTAKE-V2-BUILD-V1 -- THE ONE reusable register-patient step set
 * (must-match 12/13: SEARCH FIRST is the only creation door, and the
 * register step is ONE component -- the arrival wizard hosts it
 * appointment-anchored (CheckinIdentityStep), and the standalone
 * search-first door hosts it person-only (PatientCreatePage, founder
 * ruling D7: register the person, land on the patient record, create NO
 * visit). Never a second registration path.
 *
 * PRESENTATIONAL ONLY: every piece here is a pure function of props
 * (values + callbacks), SSR-renderable (the repo test idiom). State,
 * fetching, drafts, and submits live in the hosts; validation lives in
 * register-wizard-model.ts (D3: per step, on Continue, beside the field).
 *
 * Display language: the polished mockup copy VERBATIM
 * (docs/design/intake-registration-v2/mockups/01-arrival-wizard.html;
 * word discipline is the law -- brief section 17). No status token, no
 * verdict token, no error code ever renders here (brief 12.2).
 */

import { Fragment, type ReactNode } from 'react';

import {
  AcButton,
  checklistStates,
  RECORD_HEADER_REGISTERED_AT_LABEL,
} from '@/dashboard/ui';
import {
  ARRIVAL_ALERT_LABEL,
  ARRIVAL_EXTERNAL_MR_HINT,
  ARRIVAL_LABEL_EXTERNAL_MR,
  ARRIVAL_CARD_REASON,
  ARRIVAL_CONSENT_SIGN_PAPER,
  ARRIVAL_CONSENT_SIGN_SCREEN,
  ARRIVAL_LABEL_GUARDIAN,
  ARRIVAL_LABEL_MOBILE,
  ARRIVAL_LABEL_RELATIONSHIP,
  ARRIVAL_MINOR_NOTE,
  ARRIVAL_MOBILE_EXAMPLE,
  ARRIVAL_NO_PATIENT_FOUND,
  ARRIVAL_NONE_OF_THESE,
  ARRIVAL_QUESTION_REASON,
  ARRIVAL_REGISTER_NEW,
  ARRIVAL_RELATIONSHIP_OPTIONS,
  ARRIVAL_REQUIRED_LEGEND,
  ARRIVAL_SEARCH_BUTTON,
  ARRIVAL_SEARCH_PLACEHOLDER,
  ARRIVAL_SEARCH_TO_CONTINUE,
  ARRIVAL_SELECT_CARD,
  ARRIVAL_TAP_TO_EDIT,
  type ArrivalFieldError,
  type ArrivalPickedPatient,
  type ArrivalReviewRow,
  type ArrivalWizardForm,
  type RegistrationConsentPath,
  type RegistrationReasonChip,
  type RegistrationRequiredField,
} from '@/dashboard/patients/register-wizard-model';
import {
  PENDING_PHOTO_LINE_CONSENT_STEP,
  PendingPhotoBlock,
} from '@/dashboard/patients/consent-photo';
import {
  ENQUIRY_CONTINUE_ACTION,
  type EnquiryGlanceCard,
  type PatientCardDeskFacts,
} from '@/dashboard/patients/patient-master-view';
import { formatIstDateDdMmYyyy, parseDdMmYyyyToIso } from '@/lib/datetime-ist';
import { clinicLabelOf } from '@/dashboard/clinic-context';
import { buildAgeView } from '@/lib/age';
import {
  RegistrationContractSections,
  type RegistrationFieldValues,
  type RegistrationSectionsPrefill,
} from '@/dashboard/appointments/RegistrationContractSections';
import { ageDisplayText } from '@/dashboard/appointments/patient-intake-mode-view';
import { formatMinorUnitsInr } from '@/dashboard/billing/billing-entry-view';
import { humanizeSource } from '@/walkin/ExistingLeadMatches';

// ---- Small shared pieces ---------------------------------------------------

function errorFor(
  errors: readonly ArrivalFieldError[],
  field: string,
): string | null {
  return errors.find((e) => e.field === field)?.message ?? null;
}

/** A red star beside a required label + one legend at the top of the step
 *  (brief 6.4 rendering 1). A required field with no star is a defect. */
export function RequiredMark(): JSX.Element {
  return (
    <span className="ac-wizard__req" aria-hidden="true">
      *
    </span>
  );
}

export function RequiredLegend(): JSX.Element {
  return (
    <p className="ac-wizard__req-legend" data-testid="arrival-required-legend">
      <span className="ac-wizard__req" aria-hidden="true">
        *
      </span>{' '}
      {ARRIVAL_REQUIRED_LEGEND}
    </p>
  );
}

function FieldError({ message }: { readonly message: string }): JSX.Element {
  return (
    <span className="ac-wizard__field-error" role="alert">
      <span className="ac-wizard__field-error-glyph" aria-hidden="true">
        !
      </span>
      {message}
    </span>
  );
}

/** The wizard's step header block: title context + `Step N of M` + the
 *  bar-plus-words progress (V2: visible on every step, M never changes). */
export function ArrivalStepHeader({
  title,
  context,
  stepNumber,
  stepCount,
}: {
  readonly title: string;
  readonly context: string | null;
  readonly stepNumber: number;
  readonly stepCount: number;
}): JSX.Element {
  const segments = Array.from({ length: stepCount }, (_, i) => i + 1);
  return (
    <div className="ac-wizard__head" data-testid="arrival-step-header">
      <div className="ac-wizard__head-row">
        <h3 className="ac-wizard__title">{title}</h3>
        <span className="ac-wizard__step" data-testid="arrival-step-indicator">
          Step {stepNumber} of {stepCount}
        </span>
      </div>
      {context !== null && context !== '' ? (
        <p className="ac-wizard__context">{context}</p>
      ) : null}
      <div className="ac-wizard__progress" aria-hidden="true">
        {segments.map((n) => (
          <span
            key={n}
            className={
              n <= stepNumber
                ? 'ac-wizard__progress-seg ac-wizard__progress-seg--done'
                : 'ac-wizard__progress-seg'
            }
          />
        ))}
      </div>
    </div>
  );
}

/** The amber blocked-state line above Continue (rendering 2 of 6.4). */
export function StillNeededLine({ line }: { readonly line: string }): JSX.Element | null {
  if (line === '') return null;
  return (
    <p className="ac-wizard__still-needed" role="status" data-testid="arrival-still-needed">
      <span aria-hidden="true">!</span> {line}
    </p>
  );
}

/** One segmented control (buttons, aria-pressed; NOTHING pre-selected
 *  unless the value says so -- F8). */
export function SegmentedControl({
  options,
  value,
  onPick,
  disabled,
  testId,
  ariaLabel,
}: {
  readonly options: ReadonlyArray<{ readonly value: string; readonly label: string }>;
  readonly value: string;
  readonly onPick: (value: string) => void;
  readonly disabled?: boolean;
  readonly testId?: string;
  readonly ariaLabel?: string;
}): JSX.Element {
  return (
    <div
      className="ac-wizard__segmented"
      role="group"
      aria-label={ariaLabel}
      {...(testId ? { 'data-testid': testId } : {})}
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className={
            o.value === value
              ? 'ac-wizard__segment ac-wizard__segment--on'
              : 'ac-wizard__segment'
          }
          aria-pressed={o.value === value}
          disabled={disabled}
          onClick={() => onPick(o.value)}
          data-testid={testId ? `${testId}-${o.value}` : undefined}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ---- A1: the search step (SEARCH FIRST -- the only creation door) ----------

export interface SearchResultCardView {
  readonly patientId: string;
  readonly umr: string;
  readonly fullName: string;
  readonly gender: string;
  readonly ageYears: number | null;
  readonly city: string | null;
  /** 119-CLINIC-OPERATING-SPINE-V1 Phase 3: the branch this patient is
   *  REGISTERED at. Both fields ride the wire already (PatientCandidateCard);
   *  every host simply dropped them on the way to this card, so a result
   *  reached across the network read exactly like a local one -- and the CITY
   *  sat where an operator expects the clinic, which is how a city came to be
   *  mistaken for one. OPTIONAL so an older or partial payload renders the
   *  card unchanged rather than crashing (the tolerant-read idiom, LES-057). */
  readonly registeredClinicId?: number | null;
  readonly registeredClinicLabel?: string | null;
}

export interface RegisterSearchStepProps {
  readonly query: string;
  readonly onQueryChange: (q: string) => void;
  readonly onSearch: () => void;
  /** null = not searched yet; [] = searched, nothing found. */
  readonly results: readonly SearchResultCardView[] | null;
  readonly searching: boolean;
  /** The honest degraded state when the search read is unavailable -- the
   *  register door stays open because the server-side duplicate check still
   *  runs first at submit (the real fence). */
  readonly searchUnavailable: string | null;
  readonly onSelect: (card: SearchResultCardView) => void;
  readonly onRegisterNew: () => void;
  readonly busy: boolean;
}

/** ONE vocabulary for this fact across every surface: the patients list
 *  already calls its column "Registered at", so the card says the same. */
/**
 * THE ONE wording for "where did this patient enter the network", shared with
 * the record header so the same fact reads identically on every surface.
 * Phase 3 shipped these words on the search card; Phase 4 found the record
 * header printing the bare word "Clinic" for the same fact. One fact, one
 * label, one source of the string.
 */
export const CARD_REGISTERED_AT_LABEL = RECORD_HEADER_REGISTERED_AT_LABEL;

const CARD_GENDER: Readonly<Record<string, string>> = {
  female: 'Female',
  male: 'Male',
  other: 'Other',
  unknown: 'Not stated',
};

/** Exported for the home global-search modal (FRONT-OFFICE-OS-V2 FO-4):
 *  the SAME Tier-0 candidate card, one grammar for every search surface.
 *
 *  RECEPTION-FIX-AND-POLISH-BATCH-V1 (FD-15): `facts` (optional -- absent
 *  wire renders the Tier-0 card byte-identical) adds the desk's decision
 *  facts as LABELED FIELDS (FIELDS-NOT-SENTENCES): last visit (dd/mm/yyyy,
 *  FD-33) + doctor, last treatment (billing-plane label), per-episode
 *  outstanding (R-Q04: a LIST, never an invented total), next appointment.
 *  Money renders full-precision INR through THE ONE formatter. */
export function RecordCard({
  card,
  onSelect,
  onBook,
  busy,
  facts = null,
}: {
  readonly card: SearchResultCardView;
  readonly onSelect: (card: SearchResultCardView) => void;
  /** D4 (RECEPTION-WALK-CORRECTIVE-V1): OPTIONAL booking affordance. Passed
   *  ONLY by the walk-in global search (the "search existing patient -> book" a
   *  new visit flow); absent on the check-in + leads hosts, which stay
   *  byte-identical (one primary "Select", record-open path untouched). When
   *  present, Book is the primary action and Select demotes to secondary. */
  readonly onBook?: (card: SearchResultCardView) => void;
  readonly busy: boolean;
  readonly facts?: PatientCardDeskFacts | null;
}): JSX.Element {
  return (
    <li className="ac-record-card" data-testid="arrival-record-card">
      <dl className="ac-record-card__facts">
        <div className="ac-record-card__fact">
          <dt>Name</dt>
          <dd>{card.fullName}</dd>
        </div>
        <div className="ac-record-card__fact">
          <dt>ID</dt>
          <dd className="ac-record-card__num">{card.umr}</dd>
        </div>
        <div className="ac-record-card__fact">
          <dt>Age</dt>
          <dd className="ac-record-card__num">{card.ageYears ?? '-'}</dd>
        </div>
        <div className="ac-record-card__fact">
          <dt>Gender</dt>
          <dd>{CARD_GENDER[card.gender] ?? card.gender}</dd>
        </div>
        {/* REF-001 (APPROVED, binding for this surface): the branch is an
            ORDINARY labelled contact fact, level with the others -- not an
            admin detail, not a tooltip, not grey sub-text. A patient from
            another branch is a NORMAL result and reads as one; we LABEL it
            rather than flagging it, which is the inversion this vertical is
            built on. Placed immediately BEFORE City so the two location facts
            sit together and the clinic reads first.
            Deliberately NOT the ClinicScopeLabel primitive: that one means
            "which clinic is this SURFACE showing" (the lens). This is "which
            clinic does this RECORD belong to". Same word, different question --
            conflating the two is the exact defect this package exists to close,
            so the card's own registered dt/dd grammar carries it instead. */}
        {card.registeredClinicId !== null &&
        card.registeredClinicId !== undefined ? (
          <div className="ac-record-card__fact">
            <dt>{CARD_REGISTERED_AT_LABEL}</dt>
            <dd data-testid="card-registered-clinic">
              {clinicLabelOf(card.registeredClinicLabel)}
            </dd>
          </div>
        ) : null}
        {card.city !== null && card.city !== '' ? (
          <div className="ac-record-card__fact">
            <dt>City</dt>
            <dd>{card.city}</dd>
          </div>
        ) : null}
        {facts !== null && facts.lastVisitAt !== null ? (
          <div className="ac-record-card__fact">
            <dt>{facts.visitHistoryAvailability === 'partial' ? 'Last known visit' : 'Last visit'}</dt>
            <dd data-testid="card-last-visit">
              {formatIstDateDdMmYyyy(facts.lastVisitAt)}
              {facts.lastVisitDoctor !== null
                ? ` - ${facts.lastVisitDoctor}`
                : ''}
            </dd>
          </div>
        ) : null}
        {facts !== null && facts.lastVisitTreatment !== null ? (
          <div className="ac-record-card__fact">
            <dt>Last treatment</dt>
            <dd data-testid="card-last-treatment">
              {facts.lastVisitTreatment}
              {facts.lastVisitTreatmentMoreCount > 0
                ? ` +${facts.lastVisitTreatmentMoreCount} more`
                : ''}
            </dd>
          </div>
        ) : null}
        {facts !== null && facts.nextAppointmentAt !== null ? (
          <div className="ac-record-card__fact">
            <dt>Next appointment</dt>
            <dd data-testid="card-next-appointment">
              {formatIstDateDdMmYyyy(facts.nextAppointmentAt)}
            </dd>
          </div>
        ) : null}
        {facts !== null && facts.outstandingEpisodes.length > 0 ? (
          <div className="ac-record-card__fact ac-record-card__fact--wide">
            <dt>Outstanding</dt>
            <dd data-testid="card-outstanding">
              {facts.outstandingEpisodes.map((ep, i) => (
                <span key={i} className="ac-record-card__episode">
                  {ep.outstandingMinorUnits !== null
                    ? formatMinorUnitsInr(ep.outstandingMinorUnits)
                    : 'Amount not set'}
                  {' ('}
                  {formatIstDateDdMmYyyy(ep.appointmentTimeIso)}
                  {')'}
                </span>
              ))}
              {facts.outstandingEpisodesTruncated ? (
                <span className="ac-record-card__episode">more on record</span>
              ) : null}
            </dd>
          </div>
        ) : null}
      </dl>
      {onBook !== undefined ? (
        <AcButton
          size="sm"
          variant="primary"
          disabled={busy}
          onClick={() => onBook(card)}
          data-testid={`arrival-book-${card.patientId}`}
        >
          Book appointment
        </AcButton>
      ) : null}
      <AcButton
        size="sm"
        variant={onBook !== undefined ? 'secondary' : 'primary'}
        disabled={busy}
        onClick={() => onSelect(card)}
        data-testid={`arrival-select-${card.patientId}`}
      >
        {ARRIVAL_SELECT_CARD}
      </AcButton>
    </li>
  );
}

/**
 * FD-16/FD-27 (RECEPTION LAWS 1+2): the ENQUIRY card -- rendered when a
 * search matched no patient but the person has an open enquiry. Desk
 * vocabulary only (never "lead", never a stage token); Tier-0
 * administrative facts; ONE action: continue the registration (the journey
 * reattaches server-side through the bridge).
 */
export function EnquiryCard({
  card,
  onContinue,
  busy,
}: {
  readonly card: EnquiryGlanceCard;
  readonly onContinue: (card: EnquiryGlanceCard) => void;
  readonly busy: boolean;
}): JSX.Element {
  return (
    <li className="ac-record-card" data-testid="enquiry-card">
      <dl className="ac-record-card__facts">
        <div className="ac-record-card__fact">
          <dt>Name</dt>
          <dd>{card.name ?? 'Not captured'}</dd>
        </div>
        {card.city !== null && card.city !== '' ? (
          <div className="ac-record-card__fact">
            <dt>City</dt>
            <dd>{card.city}</dd>
          </div>
        ) : null}
        {card.enquiredAt !== null ? (
          <div className="ac-record-card__fact">
            <dt>Enquired</dt>
            <dd>{formatIstDateDdMmYyyy(card.enquiredAt)}</dd>
          </div>
        ) : null}
        {card.source !== '' ? (
          <div className="ac-record-card__fact">
            <dt>Source</dt>
            <dd>{humanizeSource(card.source)}</dd>
          </div>
        ) : null}
        {card.clinicName !== null ? (
          <div className="ac-record-card__fact">
            <dt>Clinic</dt>
            <dd>{card.clinicName}</dd>
          </div>
        ) : null}
      </dl>
      <AcButton
        size="sm"
        variant="primary"
        disabled={busy}
        onClick={() => onContinue(card)}
        data-testid={`enquiry-continue-${card.leadId}`}
      >
        {ENQUIRY_CONTINUE_ACTION}
      </AcButton>
    </li>
  );
}

/**
 * The search-first step (A1a/A1b/A1c + the S7 dense projection at 3+ rows).
 * The register door renders ONLY after a search returned nothing suitable
 * (A1c), or through the quiet `None of these` under results -- never before
 * a search (C13). Cards carry labelled facts BEFORE selection; nothing is
 * pre-selected (V24 / FAMILY-NEVER-AUTO-SELECTS).
 */
export function RegisterSearchStep({
  query,
  onQueryChange,
  onSearch,
  results,
  searching,
  searchUnavailable,
  onSelect,
  onRegisterNew,
  busy,
}: RegisterSearchStepProps): JSX.Element {
  const dense = results !== null && results.length >= 3;
  return (
    <div className="ac-wizard__panel" data-testid="arrival-search-step">
      <div className="ac-wizard__card ac-wizard__search-card">
        <input
          className="ac-wizard__search-input"
          type="search"
          placeholder={ARRIVAL_SEARCH_PLACEHOLDER}
          value={query}
          aria-label={ARRIVAL_SEARCH_PLACEHOLDER}
          onChange={(e) => onQueryChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onSearch();
            }
          }}
          data-testid="arrival-search-input"
        />
        <AcButton
          size="sm"
          variant={results === null ? 'primary' : 'secondary'}
          disabled={busy || searching || query.trim() === ''}
          onClick={onSearch}
          data-testid="arrival-search-button"
        >
          {searching ? 'Searching...' : ARRIVAL_SEARCH_BUTTON}
        </AcButton>
      </div>

      {searchUnavailable !== null ? (
        // Degraded mode (d): a GENUINE search-service failure keeps
        // registration available -- with the honest warning, and with the
        // server's duplicate-first check still mandatory at submit (the
        // binding fence; the marker/warning is never the security boundary).
        <>
          <div className="ac-banner ac-banner--warn" role="status" data-testid="arrival-search-unavailable">
            {searchUnavailable}
          </div>
          <div className="ac-wizard__card ac-wizard__empty">
            <AcButton
              variant="primary"
              disabled={busy}
              onClick={onRegisterNew}
              data-testid="arrival-register-new"
            >
              {ARRIVAL_REGISTER_NEW}
            </AcButton>
          </div>
        </>
      ) : null}

      {searching && results === null ? (
        // S5-a: a skeleton in the shape of the answer -- a slow search never
        // reads as "no records".
        <div className="ac-wizard__card ac-wizard__skeleton" aria-hidden="true">
          <span className="ac-wizard__skeleton-bar" style={{ width: '70%' }} />
          <span className="ac-wizard__skeleton-block" />
          <span className="ac-wizard__skeleton-block" />
          <span className="ac-wizard__skeleton-bar" style={{ width: '40%' }} />
        </div>
      ) : null}

      {results !== null && results.length > 0 ? (
        dense ? (
          // S7: three or more matches earn Direction A density -- same
          // labelled columns, tighter rows, one action per row, nothing
          // pre-selected; the table scrolls inside its own container.
          <div className="ac-wizard__card">
            <p className="ac-wizard__card-title">{results.length} matches</p>
            <div className="ac-wizard__dense-scroll">
              <table className="ac-wizard__dense" data-testid="arrival-dense-results">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>ID</th>
                    <th>Age</th>
                    <th>City</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {results.map((c) => (
                    <tr key={c.patientId} data-testid="arrival-record-card">
                      <td className="ac-wizard__dense-name">{c.fullName}</td>
                      <td className="ac-record-card__num">{c.umr}</td>
                      <td className="ac-record-card__num">{c.ageYears ?? '-'}</td>
                      <td>{c.city ?? '-'}</td>
                      <td>
                        <AcButton
                          size="sm"
                          variant="primary"
                          disabled={busy}
                          onClick={() => onSelect(c)}
                          data-testid={`arrival-select-${c.patientId}`}
                        >
                          {ARRIVAL_SELECT_CARD}
                        </AcButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <ul className="ac-wizard__cards" data-testid="arrival-search-results">
            {results.map((c) => (
              <RecordCard key={c.patientId} card={c} onSelect={onSelect} busy={busy} />
            ))}
          </ul>
        )
      ) : null}

      {results !== null && results.length === 0 ? (
        // A1c: the ONLY place a register action exists, primary because the
        // evidence (a no-match search) made it the right action.
        <div className="ac-wizard__card ac-wizard__empty" data-testid="arrival-no-match">
          <p className="ac-wizard__card-title">{ARRIVAL_NO_PATIENT_FOUND}</p>
          <AcButton
            variant="primary"
            disabled={busy}
            onClick={onRegisterNew}
            data-testid="arrival-register-new"
          >
            {ARRIVAL_REGISTER_NEW}
          </AcButton>
        </div>
      ) : null}

      {results !== null && results.length > 0 ? (
        <div className="ac-wizard__quiet-row">
          <button
            type="button"
            className="ac-wizard__quiet"
            disabled={busy}
            onClick={onRegisterNew}
            data-testid="arrival-none-of-these"
          >
            {ARRIVAL_NONE_OF_THESE}
          </button>
        </div>
      ) : null}

      {results === null && !searching && searchUnavailable === null ? (
        <p className="ac-wizard__foot-note">{ARRIVAL_SEARCH_TO_CONTINUE}</p>
      ) : null}
    </div>
  );
}

// ---- A2: the details step (THE shared register fields) ---------------------

export interface RegisterDetailsFieldsProps {
  readonly visitFields?: ReactNode;
  readonly workspaceRegistration?: boolean;
  readonly choicePresentation?: 'chips' | 'select';
  readonly form: ArrivalWizardForm;
  readonly onChange: (patch: Partial<ArrivalWizardForm>) => void;
  readonly required: ReadonlySet<RegistrationRequiredField>;
  readonly errors: readonly ArrivalFieldError[];
  readonly busy: boolean;
  /** RECEPTION-OS-BUILD-V1 (Q-09=D): the org's handling-category tag
   *  vocabulary off registration config. ABSENT/[] = the select renders
   *  nowhere (also keeps an unapplied 20260803_1 column unreachable). */
  readonly handlingCategories?: readonly string[];
  /** FD-16 (UX LAW 1 NEVER ASK TWICE): when set, the mobile INPUT is
   *  replaced by this on-file confirmation line -- the phone is copied
   *  server-side (the bridge's vault reveal); the desk confirms, never
   *  re-collects. Absent = the input renders as before. */
  readonly mobileOnFile?: string | null;
  /** Legacy pinned testids ride the SAME logical controls in the arrival
   *  host ('checkpoint-name'...); the standalone host passes its own. */
  readonly testIds?: Partial<
    Record<'name' | 'mobile' | 'ageToggle' | 'age' | 'dob' | 'gender' | 'city', string>
  >;
  /** ONE-REGISTRATION-MODULE: the vocabularies the shared contract module
   *  renders (title / relation / occupation picklists + state->district
   *  geography). Absent/empty degrades each to free text (R-017). */
  readonly sectionsPrefill?: RegistrationSectionsPrefill;
}

/**
 * REGISTRATION-PARTHA-CONTRACT-V1 (ONE-REGISTRATION-MODULE), composition-parity
 * corrected by REGISTRATION-DEPLOYED-WALK-CLOSURE-V1 (founder deployed walk
 * 2026-08-13 evening): the register-details step renders EVERY field THROUGH the
 * ONE shared module (RegistrationContractSections) -- the SAME component, in the
 * SAME desk `.pim-embed` grammar, as the lead (check-in) door. The host-owned
 * identity (Full name, Old MR, Mobile) is NOT a separate `.ac-wizard__*` block:
 * it is passed into the module's desk identity SLOTS (patientIdentityField ->
 * Patient details, contactMobileField -> Contact), so the `/walkin` door and the
 * lead door compose identically -- one module, every door, no fork. Editability
 * is the only per-door difference (a fresh create has no appointment prefill to
 * source or lock). The host's ArrivalWizardForm is adapted to/from the module's
 * RegistrationFieldValues here (the only entry-point-specific glue).
 * Enforced by frontend/src/dashboard/__tests__/walkin-register-door-parity-guard.test.tsx.
 */
export function RegisterDetailsFields({
  visitFields,
  workspaceRegistration = false,
  choicePresentation = 'chips',
  form,
  onChange,
  required,
  errors,
  busy,
  handlingCategories,
  mobileOnFile = null,
  testIds,
  sectionsPrefill,
}: RegisterDetailsFieldsProps): JSX.Element {
  const t = testIds ?? {};
  const dobIso = form.ageMode === 'dob' ? parseDdMmYyyyToIso(form.dob) : null;
  const ageView = buildAgeView(
    dobIso,
    form.ageMode === 'age' && form.ageYears.trim() !== ''
      ? Number(form.ageYears)
      : null,
    Date.now(),
    null,
  );
  const moduleValues: RegistrationFieldValues = {
    contactReuse: form.contactReuse,
    nameTitle: form.nameTitle,
    lastName: form.lastName,
    ageMode: form.ageMode,
    approxAge: form.ageYears,
    dob: form.dob,
    gender: form.gender,
    email: form.email,
    altMobile: form.altMobile,
    altMobileRelation: form.altMobileRelation,
    altMobileRelationName: form.altMobileRelationName,
    relationTitle: form.relationTitle,
    relationName: form.relationName,
    city: form.city,
    addressArea: form.addressArea,
    state: form.state,
    pincode: form.pincode,
    addressDoor: form.addressDoor,
    addressStreet: form.addressStreet,
    addressLandmark: form.addressLandmark,
    addressTaluka: form.addressTaluka,
    addressCountry: form.addressCountry,
    addressExpanded: form.addressExpanded,
    occupation: form.occupation,
    handlingCategory: form.handlingCategory,
    emergencyContactName: form.emergencyContactName,
    emergencyContactRelation: form.emergencyContactRelation,
    emergencyContactPhone: form.emergencyContactPhone,
  };
  const onModuleChange = (patch: Partial<RegistrationFieldValues>): void => {
    const out: { -readonly [K in keyof ArrivalWizardForm]?: ArrivalWizardForm[K] } = {};
    if (patch.contactReuse !== undefined) out.contactReuse = patch.contactReuse;
    if (patch.nameTitle !== undefined) out.nameTitle = patch.nameTitle;
    if (patch.lastName !== undefined) out.lastName = patch.lastName;
    if (patch.ageMode !== undefined) out.ageMode = patch.ageMode;
    if (patch.approxAge !== undefined) out.ageYears = patch.approxAge;
    if (patch.dob !== undefined) out.dob = patch.dob;
    if (patch.gender !== undefined) out.gender = patch.gender;
    if (patch.email !== undefined) out.email = patch.email;
    if (patch.altMobile !== undefined) out.altMobile = patch.altMobile;
    if (patch.altMobileRelation !== undefined)
      out.altMobileRelation = patch.altMobileRelation;
    if (patch.altMobileRelationName !== undefined)
      out.altMobileRelationName = patch.altMobileRelationName;
    if (patch.relationTitle !== undefined) out.relationTitle = patch.relationTitle;
    if (patch.relationName !== undefined) out.relationName = patch.relationName;
    if (patch.city !== undefined) out.city = patch.city;
    if (patch.addressArea !== undefined) out.addressArea = patch.addressArea;
    if (patch.state !== undefined) out.state = patch.state;
    if (patch.pincode !== undefined) out.pincode = patch.pincode;
    if (patch.addressDoor !== undefined) out.addressDoor = patch.addressDoor;
    if (patch.addressStreet !== undefined) out.addressStreet = patch.addressStreet;
    if (patch.addressLandmark !== undefined)
      out.addressLandmark = patch.addressLandmark;
    if (patch.addressTaluka !== undefined) out.addressTaluka = patch.addressTaluka;
    if (patch.addressCountry !== undefined)
      out.addressCountry = patch.addressCountry;
    if (patch.addressExpanded !== undefined)
      out.addressExpanded = patch.addressExpanded;
    if (patch.occupation !== undefined) out.occupation = patch.occupation;
    if (patch.handlingCategory !== undefined)
      out.handlingCategory = patch.handlingCategory;
    if (patch.emergencyContactName !== undefined)
      out.emergencyContactName = patch.emergencyContactName;
    if (patch.emergencyContactRelation !== undefined)
      out.emergencyContactRelation = patch.emergencyContactRelation;
    if (patch.emergencyContactPhone !== undefined)
      out.emergencyContactPhone = patch.emergencyContactPhone;
    onChange(out);
  };
  // REGISTRATION-DEPLOYED-WALK-CLOSURE-V1 every-door-parity corrective (founder
  // deployed walk 2026-08-13 evening): the host-owned identity is no longer a
  // `.ac-wizard__*` FORK above the module -- it renders in the SAME desk field
  // grammar as the lead (check-in) door, THROUGH the ONE module's identity slots
  // inside a `.pim-embed` shell. Full name -> Patient details (the mockup's
  // name-field position), Mobile -> Contact, both EDITABLE (a fresh create, no
  // appointment prefill) carrying the SAME data-reg-field markers + legacy
  // testids. External MR is a person-only field (not in the lead mockup) rendered
  // in the same field grammar -- a documented FND. One module, every door.
  const nameField = (
    <section className="pim-card pim-qcard" data-reg-field="full_name">
      <h2 className="pim-q">
        Full name
        {required.has('full_name') ? <RequiredMark /> : null}
      </h2>
      <input
        className="pim-input"
        aria-label="Full name"
        placeholder={workspaceRegistration ? "Patient’s full name" : undefined}
        value={form.fullName}
        disabled={busy}
        aria-invalid={errorFor(errors, 'fullName') !== null}
        onChange={(e) => onChange({ fullName: e.target.value })}
        data-testid={t.name ?? 'register-name'}
      />
      {errorFor(errors, 'fullName') !== null ? (
        <FieldError message={errorFor(errors, 'fullName') as string} />
      ) : null}
    </section>
  );
  const externalMrField = (
    <section className="pim-card pim-qcard">
      <h2 className="pim-q">{ARRIVAL_LABEL_EXTERNAL_MR}</h2>
      <input
        className="pim-input"
        aria-label={ARRIVAL_LABEL_EXTERNAL_MR}
        value={form.externalMr}
        disabled={busy}
        onChange={(e) => onChange({ externalMr: e.target.value })}
        data-testid="register-external-mr"
      />
      <p className="pim-hint">{ARRIVAL_EXTERNAL_MR_HINT}</p>
    </section>
  );
  const mobileField = (
    <section className="pim-card pim-qcard" data-reg-field="mobile">
      <h2 className="pim-q">
        {workspaceRegistration ? 'Patient mobile' : 'Mobile'}
        {required.has('mobile') ? <RequiredMark /> : null}
      </h2>
      {mobileOnFile !== null ? (
        <p className="pim-hint" data-testid="register-mobile-on-file">
          {mobileOnFile}
        </p>
      ) : (
        <>
          <input
            className="pim-input"
            aria-label="Mobile"
            type="tel"
            inputMode="tel"
            placeholder={workspaceRegistration ? undefined : ARRIVAL_MOBILE_EXAMPLE}
            value={form.mobile}
            disabled={busy}
            aria-invalid={errorFor(errors, 'mobile') !== null}
            onChange={(e) => onChange({ mobile: e.target.value })}
            data-testid={t.mobile ?? 'register-mobile'}
          />
          {errorFor(errors, 'mobile') !== null ? (
            <FieldError message={errorFor(errors, 'mobile') as string} />
          ) : null}
          {workspaceRegistration ? null : <p className="pim-hint">10-digit Indian mobile</p>}
        </>
      )}
    </section>
  );
  return (
    <div className="pim-embed" data-testid="register-details-fields">
      <RequiredLegend />
      <div className="pim-body">
        <RegistrationContractSections
          visitFields={visitFields}
          extraAddressFields={workspaceRegistration ? externalMrField : undefined}
          workspaceRegistration={workspaceRegistration}
          choicePresentation={choicePresentation}
          values={moduleValues}
          onChange={onModuleChange}
          isRequired={(k) => (required as ReadonlySet<string>).has(k)}
          prefill={
            sectionsPrefill ?? { handlingCategories: handlingCategories ?? [] }
          }
          busy={busy}
          ageTitle="Age or date of birth"
          ageDisplay={ageDisplayText(ageView)}
          minorNote={null}
          genderRequired={required.has('gender')}
          genderHint={null}
          patientSectionTitle="Patient details"
          patientIdentityField={
            <>
              {nameField}
              {workspaceRegistration ? null : externalMrField}
            </>
          }
          contactMobileField={mobileField}
        />

      </div>
    </div>
  );
}

/** A2-R: the returning-patient confirm -- the picked record's facts as
 *  read-only labelled fields; the mobile is the one typed/searched value
 *  (the candidacy fence needs it); one quiet door flips to editing. */
export function RegisterReturningConfirm({
  picked,
  mobile,
  onMobileChange,
  errors,
  busy,
}: {
  readonly picked: ArrivalPickedPatient;
  readonly mobile: string;
  readonly onMobileChange: (v: string) => void;
  readonly errors: readonly ArrivalFieldError[];
  readonly busy: boolean;
}): JSX.Element {
  return (
    <div className="ac-wizard__panel" data-testid="arrival-returning-confirm">
      <div className="ac-review" data-testid="arrival-returning-facts">
        <div className="ac-review__row">
          <span className="ac-review__key">Name</span>
          <span className="ac-review__value">{picked.fullName}</span>
        </div>
        <div className="ac-review__row">
          <span className="ac-review__key">ID</span>
          <span className="ac-review__value ac-record-card__num">{picked.umr}</span>
        </div>
        {picked.ageYears !== null ? (
          <div className="ac-review__row">
            <span className="ac-review__key">Age</span>
            <span className="ac-review__value ac-record-card__num">{picked.ageYears}</span>
          </div>
        ) : null}
        <div className="ac-review__row">
          <span className="ac-review__key">Gender</span>
          <span className="ac-review__value">
            {CARD_GENDER[picked.gender] ?? picked.gender}
          </span>
        </div>
      </div>
      <label className="ac-wizard__field" htmlFor="arrival-returning-mobile">
        <span className="ac-wizard__label">
          {ARRIVAL_LABEL_MOBILE}
          <RequiredMark />
        </span>
        <input
          id="arrival-returning-mobile"
          type="tel"
          inputMode="tel"
          placeholder={ARRIVAL_MOBILE_EXAMPLE}
          value={mobile}
          disabled={busy}
          aria-invalid={errorFor(errors, 'mobile') !== null}
          className={
            errorFor(errors, 'mobile') !== null ? 'ac-wizard__input--error' : undefined
          }
          onChange={(e) => onMobileChange(e.target.value)}
          data-testid="arrival-returning-mobile"
        />
        {errorFor(errors, 'mobile') !== null ? (
          <FieldError message={errorFor(errors, 'mobile') as string} />
        ) : null}
      </label>
    </div>
  );
}

// ---- A3: the reason step ---------------------------------------------------

export interface RegisterReasonStepProps {
  readonly form: ArrivalWizardForm;
  readonly onChange: (patch: Partial<ArrivalWizardForm>) => void;
  readonly chips: readonly RegistrationReasonChip[];
  readonly reasonRequired: boolean;
  readonly errors: readonly ArrivalFieldError[];
  readonly busy: boolean;
  /** Legacy pinned testids (arrival host). */
  readonly complaintTestId?: string;
  readonly alertTestId?: string;
}

/** A3: one question, config-backed chips that mirror into the ONE free-text
 *  complaint (an input accelerator, never a second vocabulary), and the
 *  boolean critical-alert switch with its safety sublabel. */
export function RegisterReasonStep({
  form,
  onChange,
  chips,
  reasonRequired,
  errors,
  busy,
  complaintTestId,
  alertTestId,
}: RegisterReasonStepProps): JSX.Element {
  return (
    <div className="ac-wizard__panel" data-testid="arrival-reason-step">
      <h4 className="ac-wizard__question">{ARRIVAL_QUESTION_REASON}</h4>
      <div className="ac-wizard__card">
        <p className="ac-wizard__card-title">
          {ARRIVAL_CARD_REASON}
          {reasonRequired ? <RequiredMark /> : null}
        </p>
        {chips.length > 0 ? (
          <div className="ac-wizard__chips" data-testid="arrival-reason-chips">
            {chips.map((chip) => (
              <button
                key={chip.code}
                type="button"
                className={
                  form.complaint.trim() === chip.label
                    ? 'ac-wizard__chip ac-wizard__chip--on'
                    : 'ac-wizard__chip'
                }
                aria-pressed={form.complaint.trim() === chip.label}
                disabled={busy}
                onClick={() => onChange({ complaint: chip.label })}
                data-testid={`arrival-chip-${chip.code}`}
              >
                {chip.label}
              </button>
            ))}
          </div>
        ) : null}
        <label className="ac-wizard__field" htmlFor="arrival-complaint">
          <span className="ac-wizard__label" id="arrival-complaint-label">
            One line
          </span>
          <input
            id="arrival-complaint"
            type="text"
            maxLength={200}
            value={form.complaint}
            disabled={busy}
            aria-invalid={errorFor(errors, 'complaint') !== null}
            className={
              errorFor(errors, 'complaint') !== null ? 'ac-wizard__input--error' : undefined
            }
            onChange={(e) => onChange({ complaint: e.target.value })}
            data-testid={complaintTestId ?? 'register-complaint'}
          />
          {errorFor(errors, 'complaint') !== null ? (
            <FieldError message={errorFor(errors, 'complaint') as string} />
          ) : null}
        </label>
      </div>
      <div
        className={
          form.alertFlag ? 'ac-wizard__card ac-wizard__alert--on' : 'ac-wizard__card'
        }
      >
        <label className="ac-wizard__switch-row" htmlFor="arrival-alert">
          <input
            id="arrival-alert"
            type="checkbox"
            checked={form.alertFlag}
            disabled={busy}
            onChange={(e) => onChange({ alertFlag: e.target.checked })}
            data-testid={alertTestId ?? 'register-alert'}
          />
          <span className="ac-wizard__label">{ARRIVAL_ALERT_LABEL}</span>
        </label>
      </div>
    </div>
  );
}

// ---- A4: the consent step --------------------------------------------------

export interface RegisterConsentStepProps {
  readonly form: ArrivalWizardForm;
  readonly onChange: (patch: Partial<ArrivalWizardForm>) => void;
  readonly consentPaths: readonly RegistrationConsentPath[];
  readonly isMinor: boolean;
  readonly errors: readonly ArrivalFieldError[];
  readonly busy: boolean;
  readonly onTakePhoto: () => void;
  /** Legacy pinned testids (arrival host). */
  readonly consentTestId?: string;
  readonly guardianNameTestId?: string;
  readonly guardianRelationshipTestId?: string;
}

/**
 * A4: digital and paper as EQUAL citizens (R-CONSENT); the guardian block
 * joins THIS step for a minor with its explaining line (the anti-surprise
 * rule -- a block that appears always says why); choosing paper raises the
 * loud PENDING PHOTO state with exactly one action (C14).
 */
export function RegisterConsentStep({
  form,
  onChange,
  consentPaths,
  isMinor,
  errors,
  busy,
  onTakePhoto,
  consentTestId,
  guardianNameTestId,
  guardianRelationshipTestId,
}: RegisterConsentStepProps): JSX.Element {
  const options = [
    ...(consentPaths.includes('digital')
      ? [{ value: 'digital', label: ARRIVAL_CONSENT_SIGN_SCREEN }]
      : []),
    ...(consentPaths.includes('paper')
      ? [{ value: 'paper', label: ARRIVAL_CONSENT_SIGN_PAPER }]
      : []),
  ];
  return (
    <div className="ac-wizard__panel" data-testid="arrival-consent-step">
      <h4 className="ac-wizard__question">Consent</h4>
      {isMinor ? (
        <>
          <div className="ac-banner ac-banner--calm" data-testid="arrival-minor-note">
            <span aria-hidden="true">i</span> {ARRIVAL_MINOR_NOTE}
          </div>
          <div className="ac-wizard__card">
            <div className="ac-wizard__row">
              <label className="ac-wizard__field" htmlFor="arrival-guardian-name">
                <span className="ac-wizard__label">
                  {ARRIVAL_LABEL_GUARDIAN}
                  <RequiredMark />
                </span>
                <input
                  id="arrival-guardian-name"
                  type="text"
                  value={form.guardianName}
                  disabled={busy}
                  aria-invalid={errorFor(errors, 'guardianName') !== null}
                  className={
                    errorFor(errors, 'guardianName') !== null
                      ? 'ac-wizard__input--error'
                      : undefined
                  }
                  onChange={(e) => onChange({ guardianName: e.target.value })}
                  data-testid={guardianNameTestId ?? 'register-guardian-name'}
                />
                {errorFor(errors, 'guardianName') !== null ? (
                  <FieldError message={errorFor(errors, 'guardianName') as string} />
                ) : null}
              </label>
              <div className="ac-wizard__field">
                <span className="ac-wizard__label">
                  {ARRIVAL_LABEL_RELATIONSHIP}
                  <RequiredMark />
                </span>
                <SegmentedControl
                  ariaLabel={ARRIVAL_LABEL_RELATIONSHIP}
                  options={ARRIVAL_RELATIONSHIP_OPTIONS}
                  value={form.guardianRelationship}
                  disabled={busy}
                  onPick={(v) => onChange({ guardianRelationship: v })}
                  testId={guardianRelationshipTestId ?? 'register-guardian-relationship'}
                />
              </div>
            </div>
          </div>
        </>
      ) : null}
      <div className="ac-wizard__card">
        <div data-testid={consentTestId ?? 'register-consent-path'}>
          <SegmentedControl
            ariaLabel="How consent is given"
            options={options}
            value={form.consentPath}
            disabled={busy}
            onPick={(v) => onChange({ consentPath: v as RegistrationConsentPath })}
          />
        </div>
        {errorFor(errors, 'consentPath') !== null ? (
          <FieldError message={errorFor(errors, 'consentPath') as string} />
        ) : null}
        {form.consentPath === 'paper' ? (
          <>
            {/* RECEPTION-OS-BUILD-V1 (Q-DP-PRINT=B): the PRINT half of the
                R-CONSENT paper path -- opens the consent-form print rail in
                a new tab so the wizard (and its draft) stays put. Content =
                the org's configured template or the honest SOURCED-PENDING
                placeholder; the photo upload below stays the signed-copy
                half (C14). */}
            <a
              className="ac-btn ac-btn--secondary ac-btn--sm"
              href="/dashboard/patients/consent-print"
              target="_blank"
              rel="noreferrer"
              data-testid="arrival-consent-print-door"
            >
              Print consent form
            </a>
            <PendingPhotoBlock
              line={PENDING_PHOTO_LINE_CONSENT_STEP}
              onTakePhoto={onTakePhoto}
              disabled={busy}
              testId="arrival-pending-photo"
            />
          </>
        ) : null}
      </div>
    </div>
  );
}

// ---- A5: the review step ---------------------------------------------------

export interface RegisterReviewStepProps {
  readonly consequence: string;
  readonly rows: readonly ArrivalReviewRow[];
  readonly onEdit: (step: string) => void;
  readonly busy: boolean;
  /** The server-anchored clinic fact line (the arrival host's pinned note). */
  readonly clinicNote: string | null;
  readonly clinicNoteTestId?: string;
}

/** A5: one plain sentence of consequence, then every captured fact as a
 *  labelled row; every editable row taps back to its step (C10d). */
export function RegisterReviewStep({
  consequence,
  rows,
  onEdit,
  busy,
  clinicNote,
  clinicNoteTestId,
}: RegisterReviewStepProps): JSX.Element {
  return (
    <div className="ac-wizard__panel" data-testid="arrival-review-step">
      <div className="ac-banner ac-banner--info" data-testid="arrival-consequence">
        {consequence}
      </div>
      <div className="ac-review" data-testid="arrival-review-rows">
        {rows.map((row) =>
          row.editStep !== null ? (
            <button
              key={row.key}
              type="button"
              className="ac-review__row ac-review__row--edit"
              disabled={busy}
              onClick={() => onEdit(row.editStep as string)}
              aria-label={`Edit ${row.label.toLowerCase()}`}
              data-testid={`arrival-review-${row.key}`}
            >
              <span className="ac-review__key">{row.label}</span>
              <span className="ac-review__value">{row.value}</span>
            </button>
          ) : (
            <div key={row.key} className="ac-review__row" data-testid={`arrival-review-${row.key}`}>
              <span className="ac-review__key">{row.label}</span>
              <span className="ac-review__value">{row.value}</span>
            </div>
          ),
        )}
        {clinicNote !== null ? (
          <div className="ac-review__row" data-testid={clinicNoteTestId ?? 'register-clinic-note'}>
            <span className="ac-review__key">Clinic</span>
            <span className="ac-review__value">{clinicNote}</span>
          </div>
        ) : null}
      </div>
      <p className="ac-wizard__foot-note">{ARRIVAL_TAP_TO_EDIT}</p>
    </div>
  );
}

// ---- A6: the three-stage strip ---------------------------------------------

/** The success screen's three named stages with their AUTHORS (founder item
 *  5 / 6.6b): printing who authors each stage is what keeps registration,
 *  patient intake, and the case sheet un-blurred.
 *
 *  RECEPTION-OS-BUILD-V1 (founder finding F7; blueprint GAP 4): the
 *  positional --done/--now/--later hardcode is RETIRED -- states now come
 *  from a PROGRESS INPUT (`nowStage`, defaulting to 2 so both existing
 *  hosts render byte-identically) through the pure checklistStates
 *  derivation the registered ChecklistStepper owns. The strip keeps its own
 *  authored DOM (testid + classes are founder-walked); the STATE MACHINE is
 *  the shared one. */
export function ThreeStageStrip({
  stage1,
  stage2,
  stage3,
  nowStage = 2,
}: {
  readonly stage1: { readonly title: string; readonly author: string };
  readonly stage2: { readonly title: string; readonly author: string };
  readonly stage3: { readonly title: string; readonly author: string };
  /** Which stage is live right now (1-3). States derive; never positional. */
  readonly nowStage?: 1 | 2 | 3;
}): JSX.Element {
  const keys = ['1', '2', '3'] as const;
  const states = checklistStates(keys, String(nowStage));
  const stages = [stage1, stage2, stage3];
  return (
    <div className="ac-stage-strip" data-testid="arrival-stage-strip">
      {stages.map((stage, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <Fragment key={keys[i]}>
          {i > 0 ? (
            <span className="ac-stage-strip__arrow" aria-hidden="true">
              -&gt;
            </span>
          ) : null}
          <div
            className={`ac-stage-strip__stage ac-stage-strip__stage--${states.get(keys[i] as string) ?? 'later'}`}
            data-stage-state={states.get(keys[i] as string) ?? 'later'}
          >
            <span className="ac-stage-strip__n">Step {i + 1}</span>
            <span className="ac-stage-strip__title">{stage.title}</span>
            <span className="ac-stage-strip__author">{stage.author}</span>
          </div>
        </Fragment>
      ))}
    </div>
  );
}
